/**
 * OtpField — six-digit OTP input bound to RHF, with the prototype's slot grid.
 *
 * Wrap `InputOTP` (input-otp lib) with the `FormField` plumbing. Defaults to
 * 6 digits but accepts `length` for step-up flows where a 4-digit code suffices.
 *
 * @see prototype/onboarding/otp.html — S02
 */
import * as React from "react";
import { useFormContext, type FieldPath, type FieldValues } from "react-hook-form";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "../primitives/input-otp.js";
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormDescription,
  FormMessage,
} from "./form.js";

export interface OtpFieldProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> {
  name: TName;
  label?: React.ReactNode;
  helper?: React.ReactNode;
  length?: number;
  /** Auto-submit the surrounding form when all slots are filled. */
  autoSubmitOnComplete?: boolean;
}

export function OtpField<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({ name, label, helper, length = 6 }: OtpFieldProps<TFieldValues, TName>) {
  const { control } = useFormContext<TFieldValues>();
  return (
    <FormField<TFieldValues, TName>
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem>
          {label ? <FormLabel>{label}</FormLabel> : null}
          <FormControl>
            <InputOTP
              maxLength={length}
              autoComplete="one-time-code"
              inputMode="numeric"
              value={field.value ?? ""}
              onChange={(v: string) => field.onChange(v)}
            >
              <InputOTPGroup>
                {Array.from({ length }, (_, i) => (
                  <InputOTPSlot key={i} index={i} />
                ))}
              </InputOTPGroup>
            </InputOTP>
          </FormControl>
          {helper ? <FormDescription>{helper}</FormDescription> : null}
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
